import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../machine/selection-machine';

import { isEditableTarget } from './utils/is-editable-target';

export function handleKeyDown(capture: KeyboardDownCapture, machine: SelectionsMachine) {
  // If another system already claimed this keystroke, ignore it completely.
  if (capture.properties.consumed) {
    return;
  }

  const state = machine.getState();

  if (state.isEditing || !state.focusedCell) {
    return;
  }

  const { key, event: domEvent } = capture.properties;

  // If the user is focused on an external input (like a comment box or side panel),
  // we completely ignore the keystroke so they don't accidentally edit/clear the grid.
  if (isEditableTarget(domEvent.target)) {
    return;
  }

  switch (key) {
    case 'Enter':
      // prevent default so the browser doesn't try to submit a form or scroll
      domEvent.preventDefault();

      machine.dispatch({
        type: 'startEditing',
        cell: state.focusedCell,
        source: 'keyboard',
      });

      capture.properties.consumed = true;

      break;

    case 'Backspace':
    case 'Delete':
      if (!domEvent.ctrlKey && !domEvent.metaKey) {
        domEvent.preventDefault();

        machine.dispatch({
          type: 'clearCells',
          source: 'keyboard',
        });

        capture.properties.consumed = true;
      }
      break;

    case 'Control':
    case 'Meta':
      break;

    case 'Escape':
      domEvent.preventDefault();

      machine.dispatch({
        type: 'deselectAll',
        source: 'keyboard',
      });

      capture.properties.consumed = true;

      break;

    default:
      // THE MODIFIER GUARD: Ensure Ctrl/Cmd are NOT pressed
      if (key.length === 1 && !domEvent.ctrlKey && !domEvent.metaKey) {
        machine.dispatch({
          type: 'startEditing',
          cell: state.focusedCell,
          source: 'keyboard',
        });

        // Notice we do NOT preventDefault here.
        // We want the 'a' or '9' to naturally flow into the <input>
        // that is about to render on the screen.
        capture.properties.consumed = true;
      }
      break;
  }
}
